import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Icon, Skeleton } from "@nexus/ui";
import type {
  ChangelogChannel,
  ChangelogDigest,
} from "@/features/changelog/api/changelog.api";
import {
  changelogCandidatesQueryOptions,
  changelogDigestsQueryOptions,
  changelogRecipientsQueryOptions,
  changelogSettingsQueryOptions,
} from "@/lib/queries";
import {
  useApproveChangelogDigest,
  useComposeChangelogDigest,
  useDeleteChangelogDigest,
  useRegenerateChangelogDigest,
  useRevokeChangelogDigestApproval,
  useSaveChangelogDigest,
  useSendChangelogDigest,
} from "@/features/changelog/hooks/useChangelogDigestActions";
import { useChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import { changelogErrorMessage } from "@/features/changelog/lib/changelog-errors";
import { DIGEST_CHANNELS, formatDeployedAt } from "@/features/changelog/lib/changelog-display";
import { ChangelogCoverageList } from "./shared/ChangelogCoverageList";
import { ChangelogNotice } from "./shared/ChangelogNotice";
import { ChangelogComposeSheet } from "./ChangelogComposeSheet";
import { ChangelogDetailSheet } from "./ChangelogDetailSheet";
import { ChangelogRecipientsSheet } from "./ChangelogRecipientsSheet";
import { ChangelogRoundupsTable } from "./ChangelogRoundupsTable";
import { ChangelogSendDialog } from "./ChangelogSendDialog";

/**
 * The company-facing half. A roundup is assembled by hand from what has
 * shipped, merged into one narrative, then approved and sent — so the mailing
 * list gets one considered message instead of one per deploy.
 */
export function ChangelogRoundupsPage() {
  const { t, i18n } = useTranslation();
  const permissions = useChangelogPermissions();

  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isRecipientsOpen, setIsRecipientsOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    ...changelogDigestsQueryOptions(),
    enabled: permissions.canListDigests,
  });

  const { data: candidates, isFetching: isLoadingCandidates } = useQuery({
    ...changelogCandidatesQueryOptions(),
    enabled: permissions.canListCandidates && isComposeOpen,
  });

  const { data: recipients } = useQuery({
    ...changelogRecipientsQueryOptions(),
    enabled: permissions.canListRecipients && (isSendOpen || isRecipientsOpen),
  });

  // Only to tell the send dialog whether in-app is on hold. Without the read
  // permission the dialog leaves the channel pickable and the API refuses it
  // with the same explanation.
  const { data: settings } = useQuery({
    ...changelogSettingsQueryOptions(),
    enabled: permissions.canViewSettings && isSendOpen,
  });

  const compose = useComposeChangelogDigest();
  const regenerate = useRegenerateChangelogDigest();
  const save = useSaveChangelogDigest();
  const approve = useApproveChangelogDigest();
  const revoke = useRevokeChangelogDigestApproval();
  const send = useSendChangelogDigest();
  const discard = useDeleteChangelogDigest();

  const digests = data?.digests ?? [];
  const openDigest = digests.find((digest) => digest.id === openId) ?? null;

  const isBusy =
    regenerate.isPending ||
    approve.isPending ||
    revoke.isPending ||
    send.isPending ||
    discard.isPending;

  const actionError =
    regenerate.error ?? approve.error ?? revoke.error ?? send.error ?? discard.error;

  function handleApprove(digest: ChangelogDigest) {
    approve.mutate(
      { digestId: digest.id, expectedUpdatedAt: digest.updatedAt },
      {
        onSuccess: () => {
          toast.success(
            t("admin.changelog.roundups.approvedToast", "{{title}} approved", {
              title: digest.title,
            }),
            {
              action: {
                label: t("admin.changelog.undo", "Undo"),
                onClick: () => revoke.mutate(digest.id),
              },
            },
          );
        },
      },
    );
  }

  function handleSend(digestId: string, channels: ChangelogChannel[], force = false) {
    send.mutate(
      { digestId, channels, force },
      {
        onSuccess: () => {
          setIsSendOpen(false);
          toast.success(t("admin.changelog.roundups.sentToast", "The roundup is on its way"));
        },
      },
    );
  }

  function handleDiscard(digest: ChangelogDigest) {
    // Discarding is the one irreversible action here, and it silently returns
    // the roundup's deploys to the compose list, so it is worth a question.
    if (
      !window.confirm(
        t(
          "admin.changelog.roundups.discardConfirm",
          "Discard {{title}}? Its releases go back to the compose list.",
          { title: digest.title },
        ),
      )
    ) {
      return;
    }
    discard.mutate(digest.id, { onSuccess: () => setOpenId(null) });
  }

  function closeEditor() {
    if (
      isEditorDirty &&
      !window.confirm(
        t("admin.changelog.discardConfirm", "Discard your unsaved changes to these notes?"),
      )
    ) {
      return false;
    }
    setIsEditing(false);
    setIsEditorDirty(false);
    save.reset();
    return true;
  }

  if (!permissions.canListDigests) {
    return (
      <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
        {t("admin.changelog.roundups.noAccess", "You do not have access to roundups.")}
      </p>
    );
  }

  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
          {t(
            "admin.changelog.roundups.intro",
            "One message for everyone who does not read the release channel.",
          )}
        </p>

        <span className="flex-1" />

        {permissions.canListRecipients ? (
          <Button
            variant="ghost"
            size="sm"
            iconStart="users"
            className="h-8 rounded-sm text-xs"
            onClick={() => setIsRecipientsOpen(true)}
          >
            {t("admin.changelog.recipients.open", "Mailing list")}
          </Button>
        ) : null}

        {permissions.canCompose ? (
          <Button
            size="sm"
            iconStart="plus"
            className="h-8 rounded-sm text-xs"
            onClick={() => setIsComposeOpen(true)}
          >
            {t("admin.changelog.roundups.compose", "Compose roundup")}
          </Button>
        ) : null}
      </div>

      {actionError ? (
        <div className="shrink-0 rounded-md border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
          {changelogErrorMessage(
            actionError,
            t("admin.changelog.actionError", "That did not work. Try again."),
          )}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-card">
        {isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
            <p className="m-0 text-sm text-muted-foreground">
              {t("admin.changelog.roundups.loadError", "Could not load roundups.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t("admin.changelog.retry", "Try again")}
            </Button>
          </div>
        ) : digests.length === 0 ? (
          <ChangelogNotice
            title={t("admin.changelog.roundups.emptyTitle", "No roundups yet")}
            description={t(
              "admin.changelog.roundups.empty",
              "Pick the releases worth talking about and they get merged into one message for the mailing list and the in-app banner.",
            )}
            action={
              permissions.canCompose ? (
                <Button size="sm" iconStart="plus" onClick={() => setIsComposeOpen(true)}>
                  {t("admin.changelog.roundups.compose", "Compose roundup")}
                </Button>
              ) : null
            }
          />
        ) : (
          <ChangelogRoundupsTable
            digests={digests}
            selectedId={openId}
            onSelect={(digest) => {
              setOpenId(digest.id);
              setIsEditing(false);
              setIsEditorDirty(false);
              save.reset();
            }}
          />
        )}
      </div>

      {openDigest ? (
        <ChangelogDetailSheet
          kind="roundup"
          isOpen
          onOpenChange={(open) => {
            if (open) return;
            if (isEditing && !closeEditor()) return;
            setOpenId(null);
          }}
          title={openDigest.title}
          meta={t("admin.changelog.roundups.meta", {
            count: openDigest.entries.length,
            at: formatDeployedAt(openDigest.createdAt, i18n.language),
            by: openDigest.createdByName ?? "—",
          })}
          target={openDigest}
          extra={<ChangelogCoverageList entries={openDigest.entries} />}
          channels={DIGEST_CHANNELS}
          capabilities={{
            edit: permissions.canEditDigest,
            approve: permissions.canApproveDigest,
            revokeApproval: permissions.canRevokeDigestApproval,
            send: permissions.canSendDigest,
            regenerate: permissions.canRegenerateDigest,
            discard: permissions.canDeleteDigest,
          }}
            isBusy={isBusy}
            isEditing={isEditing}
            isDirty={isEditorDirty}
            isSaving={save.isPending}
          saveError={
            save.error
              ? changelogErrorMessage(
                  save.error,
                  t("admin.changelog.saveError", "Could not save these notes."),
                )
              : null
          }
          onStartEdit={() => {
            setIsEditing(true);
            setIsEditorDirty(false);
            save.reset();
          }}
          onCancelEdit={closeEditor}
          onDirtyChange={setIsEditorDirty}
          onSave={(input) =>
            save.mutate(
              {
                digestId: openDigest.id,
                title: input.title ?? openDigest.title,
                headline: input.headline,
                sections: input.sections,
                expectedUpdatedAt: openDigest.updatedAt,
              },
              {
                onSuccess: () => {
                  setIsEditing(false);
                  setIsEditorDirty(false);
                },
              },
            )
          }
          onRegenerate={() => regenerate.mutate(openDigest.id)}
          onApprove={() => handleApprove(openDigest)}
          onRevokeApproval={() => revoke.mutate(openDigest.id)}
          onDiscard={() => handleDiscard(openDigest)}
          onOpenSend={() => setIsSendOpen(true)}
          onRetryChannel={(channel, force) => handleSend(openDigest.id, [channel], force)}
        />
      ) : null}

      {openDigest && isSendOpen ? (
        <ChangelogSendDialog
          targetId={openDigest.id}
          digestId={openDigest.id}
          canPreviewDigestEmail={permissions.canPreviewDigestEmail}
          title={t("admin.changelog.roundups.sendTitle", "Send {{title}}", {
            title: openDigest.title,
          })}
          target={openDigest}
          channels={DIGEST_CHANNELS}
          isOpen
          isSending={send.isPending}
          sendError={
            send.error
              ? changelogErrorMessage(
                  send.error,
                  t("admin.changelog.send.error", "Could not start the delivery."),
                )
              : null
          }
          recipients={recipients}
          inAppBlockedReason={
            settings?.userFacingAnnouncements === "HOLD" ? "inAppOnHold" : null
          }
          onManageRecipients={
            permissions.canListRecipients
              ? () => {
                  setIsSendOpen(false);
                  setIsRecipientsOpen(true);
                }
              : undefined
          }
          onOpenChange={setIsSendOpen}
          onSend={(channels) => handleSend(openDigest.id, channels)}
        />
      ) : null}

      <ChangelogComposeSheet
        isOpen={isComposeOpen}
        candidates={candidates}
        isLoading={isLoadingCandidates}
        isComposing={compose.isPending}
        composeError={
          compose.error
            ? changelogErrorMessage(
                compose.error,
                t("admin.changelog.compose.error", "Could not start the merge."),
              )
            : null
        }
        onOpenChange={(open) => {
          setIsComposeOpen(open);
          if (!open) compose.reset();
        }}
        onCompose={(input) =>
          compose.mutate(input, {
            onSuccess: () => {
              setIsComposeOpen(false);
              toast.success(t("admin.changelog.compose.startedToast", "Merging the picked items…"));
            },
          })
        }
      />

      <ChangelogRecipientsSheet
        isOpen={isRecipientsOpen}
        onOpenChange={setIsRecipientsOpen}
        permissions={permissions}
      />
    </>
  );
}
